import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonToolbar,
} from "@ionic/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PageHeader from "./PageHeader";
import { logCustomMessage } from "../util/logger";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { findEmployee } from "../api/Employees";
import { getUserId } from "../util/UserHelper";
import { ColumnsWithGroupData, getColumnsWithGroup } from "../api/Windows";
import { GetColumnsWithGroupField } from "../models/windows/GetColumnsWithGroup";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { SearchData } from "./ISSElement";
import { barcodeOutline, barcodeSharp } from "ionicons/icons";
import { useBarcodeManager } from "../hooks/useBarcodeManager";
import { findPO } from "../api/PurchaseOrders";
import POReceiptFields from "./POReceiptFields";
import { extractFieldsAndValues } from "../util/MetadataHelper";

const EMPLOYEE_ID = "EmployeeId";
const CF_PURCHASE_ORDER = "CfPurchaseOrder";
const BILL_OF_LADING = "BillOfLading";
const COMMENTS = "Comments";

const POReceipts: React.FC = () => {
  const [showLoading, setShowLoading] = useState(false);
  const { translations } = useContext(TranslationsContext);

  const [metadata, setMetadata] = useState<GetColumnsWithGroupField[]>([]);
  const [metadataError, setMetadataError] = useState(false);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [values, setValues] = useState<{ [key: string]: any }>({});
  const [showError, setShowError] = useState<any>({});
  const [customValues, setCustomValues] = useState({});

  const handlePOBarCodeScan = useCallback((value: string) => {
    setValues((pre) => ({ ...pre, CfPurchaseOrder: value }));
  }, []);

  const { scanClick } = useBarcodeManager(handlePOBarCodeScan);

  const updateColumns = useCallback(async () => {
    const data: ColumnsWithGroupData = {
      WindowName: "mob_po_receipts",
      IncludeValidValues: "true",
    };
    const response = await getColumnsWithGroup(data);
    if (response.status !== 200) {
      setMetadataError(true);
    } else if (response.data) {
      const { fields, values } = extractFieldsAndValues(response);
      // Ensure each field has all required properties for GetColumnsWithGroupField
      const normalizedFields: GetColumnsWithGroupField[] = fields.map((f: any) => ({
        FieldOrder: f.FieldOrder ?? 0,
        ...f,
      }));
      setMetadata(normalizedFields);
      setValues(values);
    }
  }, []);

  const updateDefaultValues = useCallback(async () => {
    //set logged in employee as default
    const userId = await getUserId();
    setValues((pre) => ({
      ...pre,
      EmployeeId: userId, //currently logged-in user
      DateCharged: new Date().toISOString().slice(0, 10), //todays date
    }));
  }, []);

  useEffect(() => {
    //initial setup
    (async () => {
      setShowLoading(true);
      await updateColumns();
      await updateDefaultValues();
      setShowLoading(false);
    })();
  }, [updateColumns, updateDefaultValues]);

  const handleEmployeeSearch = useCallback(async (keyword: string) => {
    if (keyword.length <= 2) return [];

    const response = await findEmployee(keyword);
    const data = {
      Search: keyword,
    };
    logCustomMessage(
      "Finding employees with typeAhead",
      SeverityLevel.Information,
      "Employees/Find",
      data,
      response
    );
    if (response.status === 200) {
      return Object.entries(response.data).map(
        ([id, name]) =>
        ({
          code: id,
          description: name,
        } as SearchData)
      );
    }
    return [];
  }, []);

  const handlePOSearch = useCallback(async (keyword: string) => {
    if (keyword.length <= 2) return [];

    const response = await findPO(keyword);

    if (response.status === 200) {
      return Object.entries(response.data).map(
        ([id, name]) =>
        ({
          code: id,
          description: name,
        } as SearchData)
      );
    }
    return [];
  }, []);

  const updatePropertyValue = (propertyName: string, newVal: any) => {
    setValues((pre) => ({ ...pre, [propertyName]: newVal }));
  };

  const updateCustomValue = (field: string, value: string) => {
    let temp = JSON.parse(JSON.stringify(customValues));
    temp[field] = value;
    setCustomValues(temp);
  };

  const isInvalidForm = useMemo(() => {
    const hasRequiredFields = metadata.some((m) => m.IsRequired);
    if (!hasRequiredFields) return false;

    const requiredEntries: string[] = metadata.reduce(
      (data: string[], curr) => {
        if (curr.IsRequired && curr.PropertyName) data.push(curr.PropertyName);
        return data;
      },
      []
    );

    return requiredEntries.some((e) => !values[e]);
  }, [metadata, values]);

  const handleError = useCallback((val: any) => {
    setShowError(val);
  }, []);

  return (
    <IonPage>
      <PageHeader title={translations["lbl_mob_po_receipts"] || "PO Receipts"}>
        <IonButton onClick={scanClick} slot="end" fill="clear" size="small">
          <IonIcon
            ios={barcodeOutline}
            md={barcodeSharp}
            slot="icon-only"
          ></IonIcon>
        </IonButton>
      </PageHeader>

      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />

      <IonContent fullscreen>
        {metadataError && (
          <IonLabel color="danger">
            {translatedMessages["ErrRetrievingFields"]?.MessageText ||
              "Error retrieving the fields for the creation form"}
          </IonLabel>
        )}

        <IonList class="ion-padding">
          {metadata.map((field, index: number) => {
            const {
              IsRequired: required,
              PropertyName = "",
              TranslatedIdText,
              NetType,
            } = field;
            return (
              <IonItem
                lines="none"
                class="wo-list-item"
                key={`${PropertyName}-${index}`}
              >
                <IonLabel
                  className="wo-editable-input-label"
                  position={
                    NetType === "Boolean" || PropertyName === "WoType"
                      ? undefined
                      : "stacked"
                  }
                  color={required ? "danger" : undefined}
                >
                  {TranslatedIdText}
                  {required && "*"}
                </IonLabel>
                <POReceiptFields
                  error={showError}
                  field={field}
                  onChange={updatePropertyValue}
                  onCustomValueChange={updateCustomValue}
                  onEmployeeSearch={handleEmployeeSearch}
                  onError={handleError}
                  onPOSearch={handlePOSearch}
                  values={values}
                />
                {required && (
                  <IonNote
                    className="quick-wo-error-note"
                    hidden={!showError[PropertyName]}
                    slot="error"
                    color="danger"
                    key={`${PropertyName}-error`}
                  >
                    {translatedMessages["errIsRequired"]?.MessageText.replace(
                      "@1",
                      TranslatedIdText
                    )}
                  </IonNote>
                )}
              </IonItem>
            );
          })}
        </IonList>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton
              fill="solid"
              disabled={isInvalidForm}
              hidden={showLoading}
              routerLink={`/POReceipts/${values[CF_PURCHASE_ORDER]}?billOfLanding=${values[BILL_OF_LADING]}&comments=${values[COMMENTS]}&employee=${values[EMPLOYEE_ID]}`}
            >
              {translations["lbl_submit"] || "Next"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default POReceipts;
